import { GlobalContext } from 'frontastic/context/globalContext';
import { useCallback, useContext } from 'react';
import { sdk } from 'sdk';

interface InvoiceSummaryParams {
  minDate: string;
  maxDate: string;
  customerGroup?: string;
  storeCode?: string;
  transactionTypes?: string[];
  collections?: string[];
  documentTypes?: string[];
  collectionsInclude?: string[];
  businessUnits?: string[];
}
interface OrderSummaryParams {
  minDate: string;
  maxDate: string;
  billingUserCodes?: string[];
  accountCode?: string;
  userRoles?: string[];
  documentTypes?: string[];
  regionCode?: string;
  transactionTypes?: string[];
  collections?: string[];
  businessUnits?: string[];
}

interface InventorySummaryParams {
  accountCode?: string;
}

interface InventorySummaryUPCParams {
  accountCode?: string;
  inventoryCategories?: string[];
  limit?: number;
  excludeZeroOrNegative?: boolean;
  sortDirection?: 'DESC' | 'ASC';
  orderBy?: 'total_cost_value' | 'total_retail_value' | 'total_wholesale_value' | 'total_inventory_quantity';
  daysOnHandPeriod?: number;
}

interface OrderRefundsSummaryParams extends Omit<OrderSummaryParams, 'collections'> {}

interface OrderTopTenSummaryParams extends OrderSummaryParams {
  limit?: number;
  sortDirection?: 'DESC' | 'ASC';
}

interface OrderCustomerSummaryParams extends OrderSummaryParams {}

interface TicketDistributionSummaryParams {
  minDate: string;
  maxDate: string;
  accountCode?: string;
  businessUnits?: string[];
}

interface SupportCaseSummaryParams extends TicketDistributionSummaryParams {}

interface ExportCsvParams {
  fileName?: string;
  dashboardName: string;
  minDate: string;
  maxDate: string;
  businessUnits?: string[];
  transactionTypes?: string[];
  documentTypes?: string[];
  userRoles?: string[] | undefined;
  billingUserCodes?: string[] | undefined;
  endpoint: string;
  limit?: number;
}

interface ExportInventoryCsvParams {
  dashboardName: string;
  inventoryCategories?: string[];
  excludeZeroOrNegative?: boolean;
  sortDirection?: 'DESC' | 'ASC';
  orderBy?: 'total_cost_value' | 'total_retail_value' | 'total_wholesale_value' | 'total_inventory_quantity';
}

interface AllowanceSummaryParams {
  minDate: string;
  maxDate: string;
}

interface CustomersAllowanceListParams extends AllowanceSummaryParams {
  key?: string;
  firstName?: string;
  lastName?: string;
  limit?: number;
  orderBy?: string;
  sortDirection?: 'DESC' | 'ASC';
}

const useReports = () => {
  const globalContext = useContext(GlobalContext);

  const { getAllCustomerGroups, allCustomerGroups } = globalContext;

  const getInvoiceSummaries = useCallback(
    async ({
      invoicesSummaries,
      invoicesCollectionsSummaries,
    }: {
      invoicesSummaries: Record<string, InvoiceSummaryParams>[];
      invoicesCollectionsSummaries: Record<string, InvoiceSummaryParams>[];
    }): Promise<any> => {
      const res = await sdk.callAction<any>({
        actionName: 'reports/getInvoiceSummaries',
        payload: {
          invoicesSummaries,
          invoicesCollectionsSummaries,
        },
      });

      return res.isError ? ({} as any) : res.data;
    },
    [],
  );

  const getIncomeSummary = useCallback(
    async ({
      orderSummaries,
      orderRefundsSummary,
      orderTopTenSummary,
      orderCustomerSummary,
    }: {
      orderSummaries: Record<string, OrderSummaryParams>[];
      orderRefundsSummary: Record<string, OrderRefundsSummaryParams>[];
      orderTopTenSummary: Record<string, OrderTopTenSummaryParams>[];
      orderCustomerSummary: Record<string, OrderCustomerSummaryParams>[];
    }): Promise<any> => {
      const res = await sdk.callAction<any>({
        actionName: 'reports/getIncomeSummary',
        payload: {
          orderSummaries,
          orderRefundsSummary,
          orderTopTenSummary,
          orderCustomerSummary,
        },
      });

      return res.isError ? ({} as any) : res.data;
    },
    [],
  );

  const getCustomerServiceSummary = useCallback(
    async ({
      ticketDistributionsSummary,
      supportCasesSummary,
    }: {
      ticketDistributionsSummary: Record<string, TicketDistributionSummaryParams>[];
      supportCasesSummary: Record<string, SupportCaseSummaryParams>[];
    }): Promise<any> => {
      const res = await sdk.callAction<any>({
        actionName: 'reports/getCustomerServiceSummary',
        payload: {
          ticketDistributionsSummary,
          supportCasesSummary,
        },
      });

      return res.isError ? ({} as any) : res.data;
    },
    [],
  );

  const getInventorySummary = useCallback(
    async ({
      inventorySummary,
      inventorySummaryUPC,
    }: {
      inventorySummary: Record<string, InventorySummaryParams>[];
      inventorySummaryUPC: Record<string, InventorySummaryUPCParams>[];
    }): Promise<any> => {
      const res = await sdk.callAction<any>({
        actionName: 'reports/getInventorySummary',
        payload: {
          inventorySummary,
          inventorySummaryUPC,
        },
      });

      return res.isError ? ({} as any) : res.data;
    },
    [],
  );

  const getAllowanceSummary = useCallback(
    async ({
      allowanceSummary,
      customersAllowanceList,
    }: {
      allowanceSummary: Record<string, AllowanceSummaryParams>[];
      customersAllowanceList: Record<string, CustomersAllowanceListParams>[];
    }): Promise<any> => {
      const res = await sdk.callAction<any>({
        actionName: 'reports/getAllowanceSummary',
        payload: {
          allowanceSummary,
          customersAllowanceList,
        },
      });

      return res.isError ? ({} as any) : res.data;
    },
    [],
  );
  const exportReportToCsv = useCallback(async (params: ExportCsvParams): Promise<any> => {
    const res = await sdk.callAction<any>({
      actionName: 'reports/exportReportToCsv',
      payload: params,
    });

    return res.isError ? ({} as any) : res.data;
  }, []);

  const exportInventoryReportToCsv = useCallback(async (params: ExportInventoryCsvParams): Promise<any> => {
    const res = await sdk.callAction<any>({
      actionName: 'reports/exportInventoryReportToCsv',
      payload: params,
    });

    return res.isError ? ({} as any) : res.data;
  }, []);

  return {
    getAllCustomerGroups,
    allCustomerGroups,
    getInvoiceSummaries,
    getIncomeSummary,
    getCustomerServiceSummary,
    getInventorySummary,
    getAllowanceSummary,
    exportReportToCsv,
    exportInventoryReportToCsv,
  };
};

export default useReports;
